import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Shield, Lock, Heart, UserX, Trash2, Clock, Users, ArrowRight } from "lucide-react";

export default function Pledge() {
  return (
    <div className="bg-[color:var(--pws-off)] min-h-[calc(100vh-80px)] py-12 md:py-16">
      <div className="container-pws">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="worker-led-badge mx-auto mb-4">
              <Users className="h-3.5 w-3.5" />
              Built by workers, for workers
            </div>
            <h1
              className="font-['Space_Grotesk'] text-3xl sm:text-4xl font-bold text-[color:var(--pws-charcoal)]"
              data-testid="pledge-title"
            >
              Our promise to you
            </h1>
            <p className="mt-3 text-lg text-[color:var(--pws-stone)]">
              No fine print. No asterisks. Just straight talk.
            </p>
          </div>

          {/* Main pledge - conversational */}
          <Card className="border-[color:var(--pws-line)] bg-white mb-8">
            <CardContent className="p-8 md:p-10">
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-[color:var(--pws-ink)] leading-relaxed" data-testid="pledge-intro">
                  We know how it goes. Someone offers "help" and suddenly 
                  your info is everywhere—on a list, in a database, shared with 
                  people you never agreed to share with. Your autonomy traded 
                  for their funding report.
                </p>
                <p className="text-lg text-[color:var(--pws-ink)] leading-relaxed mt-4 font-medium">
                  Not here.
                </p>
              </div>

              <div className="mt-8 space-y-4" data-testid="pledge-main">
                <div className="flex items-start gap-4 p-4 bg-[color:var(--pws-cream)] rounded-xl">
                  <Shield className="h-6 w-6 text-[color:var(--pws-plum)] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-[color:var(--pws-charcoal)]">We only collect what we need</p>
                    <p className="text-sm text-[color:var(--pws-stone)] mt-1">
                      Address to ship to, size to get right. That's it.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-4 bg-[color:var(--pws-cream)] rounded-xl">
                  <Lock className="h-6 w-6 text-[color:var(--pws-plum)] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-[color:var(--pws-charcoal)]">We don't sell. We don't share.</p>
                    <p className="text-sm text-[color:var(--pws-stone)] mt-1">
                      Not to platforms. Not to law enforcement. Not to funders. Not to anyone. Period.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-4 bg-[color:var(--pws-cream)] rounded-xl">
                  <UserX className="h-6 w-6 text-[color:var(--pws-plum)] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-[color:var(--pws-charcoal)]">We don't need to know who you are</p>
                    <p className="text-sm text-[color:var(--pws-stone)] mt-1">
                      No government name. No ID. No stage name. No platform handle. No proof of work.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-4 bg-[color:var(--pws-cream)] rounded-xl">
                  <Heart className="h-6 w-6 text-[color:var(--pws-plum)] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-[color:var(--pws-charcoal)]">You can ghost us anytime</p>
                    <p className="text-sm text-[color:var(--pws-stone)] mt-1">
                      Take the underwear and never think about us again. We won't follow up unless you ask us to.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* What we collect vs don't */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <Card className="border-[color:var(--pws-line)] bg-white">
              <CardContent className="p-6">
                <h3 className="font-['Space_Grotesk'] text-lg font-semibold text-[color:var(--pws-charcoal)] mb-4">
                  What we need to ship your pair
                </h3>
                <ul className="space-y-3 text-[color:var(--pws-ink)]">
                  <li className="flex items-start gap-2">
                    <span className="text-[color:var(--pws-success)] mt-1">✓</span>
                    <span>Address (whatever name works)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[color:var(--pws-success)] mt-1">✓</span>
                    <span>Size</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-[color:var(--pws-line)] bg-white">
              <CardContent className="p-6">
                <h3 className="font-['Space_Grotesk'] text-lg font-semibold text-[color:var(--pws-charcoal)] mb-4">
                  What we never ask for
                </h3>
                <ul className="space-y-3 text-[color:var(--pws-ink)]">
                  <li className="flex items-start gap-2">
                    <span className="text-[color:var(--pws-danger)] mt-1">✕</span>
                    <span>Legal name or ID</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[color:var(--pws-danger)] mt-1">✕</span>
                    <span>Platform handles or social media</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[color:var(--pws-danger)] mt-1">✕</span>
                    <span>Proof you work in the industry</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[color:var(--pws-danger)] mt-1">✕</span>
                    <span>Your story, your reasons, your life</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Data deletion */}
          <Card className="border-[color:var(--pws-line)] bg-white mb-8">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-[color:var(--pws-cream)] rounded-lg">
                  <Trash2 className="h-5 w-5 text-[color:var(--pws-plum)]" />
                </div>
                <div>
                  <h3 className="font-['Space_Grotesk'] text-lg font-semibold text-[color:var(--pws-charcoal)]">
                    Want us to forget you existed?
                  </h3>
                  <p className="mt-2 text-[color:var(--pws-ink)]">
                    Use the code you get after requesting. Hit us up on the{" "}
                    <Link to="/contact" className="text-[color:var(--pws-plum)] underline">
                      contact page
                    </Link>{" "}
                    and we'll wipe everything. No explanation needed.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 18+ Notice */}
          <div className="p-4 bg-[color:var(--pws-cream)] rounded-xl text-center mb-8" data-testid="pledge-18-notice">
            <p className="text-[color:var(--pws-charcoal)]">
              This is for adults (18+) working in the adult industry. That's the only requirement.
            </p>
          </div>

          {/* Who we are */}
          <Card className="border-[color:var(--pws-coral)] bg-white mb-8">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-[color:var(--pws-coral)]/10 rounded-lg">
                  <Users className="h-5 w-5 text-[color:var(--pws-coral)]" />
                </div>
                <div>
                  <h3 className="font-['Space_Grotesk'] text-lg font-semibold text-[color:var(--pws-charcoal)]">
                    Built by people who get it
                  </h3>
                  <p className="mt-2 text-[color:var(--pws-ink)]">
                    Panties Without Strings is a campaign of Safe Work Coalition—a 
                    worker-led organization. We've been in the industry. We know 
                    what it's like to have "help" come with strings attached. 
                    That's exactly why this doesn't.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Last updated */}
          <div className="flex items-center justify-center gap-2 text-sm text-[color:var(--pws-stone)] mb-8">
            <Clock className="h-4 w-4" />
            <span>Last updated: January 2026</span>
          </div>

          {/* CTA */}
          <div className="text-center">
            <Button
              asChild
              size="lg"
              className="btn-warm rounded-xl"
              data-testid="pledge-request-btn"
            >
              <Link to="/request">
                Get mine
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <p className="mt-4 text-sm text-[color:var(--pws-stone)]">
              Questions?{" "}
              <Link to="/faq" className="text-[color:var(--pws-plum)] underline">
                Check the FAQ
              </Link>{" "}
              or{" "}
              <Link to="/contact" className="text-[color:var(--pws-plum)] underline">
                reach out
              </Link>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
